import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Form, Button, Table, Card, Modal } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function EmailTemplates() {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [formData, setFormData] = useState({ subject: '', textContent: '', htmlContent: '' });
    const [isEdit, setIsEdit] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [textContentPlain, setTextContentPlain] = useState(false); // Toggle for Text Content
    const [htmlContentPlain, setHtmlContentPlain] = useState(false); // Toggle for HTML Content

    const fetchTemplates = async () => {
        const response = await axios.get('https://api.eyorsolutions.com/api/templates');
        setTemplates(response.data);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isEdit) {
            await axios.put(`https://api.eyorsolutions.com/api/templates/${selectedTemplate.id}`, formData);
        } else {
            await axios.post('https://api.eyorsolutions.com/api/templates', formData);
        }
        setFormData({ subject: '', textContent: '', htmlContent: '' });
        setIsEdit(false);
        fetchTemplates();
    };

    const handleDelete = async (id) => {
        await axios.delete(`https://api.eyorsolutions.com/api/templates/${id}`);
        fetchTemplates();
    };

    const handleEdit = async (id) => {
        try {
            // Fetch the full details of the template from the backend
            const response = await axios.get(`https://api.eyorsolutions.com/api/templates/${id}`);
            const fetchedTemplate = response.data;
    
            console.log("Editing template:", fetchedTemplate); // Log for debugging
    
            // Populate the form fields, ensuring fallback to empty strings for undefined values
            setFormData({
                subject: fetchedTemplate.subject || '',
                textContent: fetchedTemplate.textContent || '',
                htmlContent: fetchedTemplate.htmlContent || '',
            });
    
            // Set the selected template and edit mode
            setSelectedTemplate(fetchedTemplate);
            setIsEdit(true);
        } catch (error) {
            console.error("Error fetching template for edit:", error);
        }
    };    

    const handlePreview = async (id) => {
        try {
            const response = await axios.get(`https://api.eyorsolutions.com/api/templates/${id}`);
            setSelectedTemplate(response.data); // Fetch the template by ID
            setShowPreview(true); // Show the preview modal
        } catch (error) {
            console.error("Error fetching the template for preview:", error);
        }
    };
    
    useEffect(() => {
        fetchTemplates();
    }, []);

    return (
        <Container className="py-4">
            <h1 className="text-center mb-4">Email Templates</h1>

            {/* Form Section */}
            <Card className="mb-4">
                <Card.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Subject</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter email subject"
                                value={formData.subject || ''}
                                onChange={(e) => {
                                    const newSubject = e.target.value;
                                    console.log("Updating subject:", newSubject); // Debugging log
                                    setFormData((prevState) => ({
                                        ...prevState,
                                        subject: newSubject,
                                    }));
                                }}
                                required
                            />
                        </Form.Group>


                        {/* Text Content with Toggle */}
                        <Form.Group className="mb-3">
                            <Form.Label>Text Content</Form.Label>
                            <Button
                                variant="secondary"
                                size="sm"
                                className="mb-2 toggle-btn"
                                onClick={() => setTextContentPlain(!textContentPlain)}
                            >
                                Switch to {textContentPlain ? 'WYSIWYG Editor' : 'Plain Text Editor'}
                            </Button>
                            {textContentPlain ? (
                                <Form.Control
                                    as="textarea"
                                    rows={6}
                                    placeholder="Enter plain text content"
                                    value={formData.textContent || ''} // Fallback to empty string
                                    onChange={(e) => setFormData((prevState) => ({
                                        ...prevState,
                                        textContent: e.target.value,
                                    }))}
                                />
                            ) : (
                                <ReactQuill
                                    theme="snow"
                                    value={formData.textContent || ''} // Fallback to empty string
                                    onChange={(value) => {
                                        console.log("Quill onChange triggered:", value); // Debugging log
                                        setFormData((prevState) => ({
                                            ...prevState,
                                            textContent: value,
                                        }));
                                    }}
                                />
                            )}
                        </Form.Group>


                        {/* HTML Content with Toggle */}
                        <Form.Group className="mb-3">
                            <Form.Label>HTML Content</Form.Label>
                            <Button
                                variant="secondary"
                                size="sm"
                                className="mb-2 toggle-btn"
                                onClick={() => setHtmlContentPlain(!htmlContentPlain)}
                            >
                                Switch to {htmlContentPlain ? 'WYSIWYG Editor' : 'Plain Text Editor'}
                            </Button>
                            {htmlContentPlain ? (
                                <Form.Control
                                    as="textarea"
                                    rows={6}
                                    placeholder="Enter raw HTML content"
                                    value={formData.htmlContent || ''} // Fallback to empty string
                                    onChange={(e) =>
                                        setFormData((prevState) => ({
                                            ...prevState,
                                            htmlContent: e.target.value,
                                        }))
                                    }
                                />
                            ) : (
                                <ReactQuill
                                    theme="snow"
                                    value={formData.htmlContent || ''} // Fallback to empty string
                                    onChange={(value) => {
                                        console.log("Quill onChange triggered for HTML:", value); // Debugging log
                                        setFormData((prevState) => ({
                                            ...prevState,
                                            htmlContent: value,
                                        }));
                                    }}
                                />
                            )}
                        </Form.Group>


                        <Button variant="primary" type="submit">
                            {isEdit ? 'Update Template' : 'Create Template'}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>

            {/* Templates List */}
            <h2>Templates</h2>
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Subject</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {templates.map((template, index) => (
                        <tr key={template.id}>
                            <td>{index + 1}</td>
                            <td>{template.subject}</td>
                            <td>
                                <Button variant="info" size="sm" onClick={() => handleEdit(template.id)} className="me-2">
                                    Edit
                                </Button>
                                <Button variant="danger" size="sm" onClick={() => handleDelete(template.id)} className="me-2">
                                    Delete
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handlePreview(template.id)}
                                >
                                    Preview
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            {/* Preview Modal */}
            <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedTemplate ? `Preview: ${selectedTemplate.subject}` : "Preview"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="preview-canvas">
                    {selectedTemplate ? (
                        <>
                            <h5>Text Content:</h5>
                            <div dangerouslySetInnerHTML={{ __html: selectedTemplate.textContent }}></div>
                            <hr />
                            <h5>HTML Content:</h5>
                            <div dangerouslySetInnerHTML={{ __html: selectedTemplate.htmlContent }}></div>
                        </>
                    ) : (
                        <p>Loading...</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPreview(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

        </Container>
    );
}

export default EmailTemplates;
